import { AfterViewChecked, ChangeDetectorRef, Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { TitleCasePipe } from '@angular/common';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { formInput, formRadioGroup, formRow, RktFormModule } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { BusinessAccountService } from '@shared/services';
import { FormSelectFieldComponent } from '@shared/components';
import { ErrorUtils, Patterns } from '@shared/utils';
import { FormModel, PermissionRoles, ServiceAccount } from '@shared/models';
import { ROLES } from '@shared/constants';

@Component({
  selector: 'app-add-service-account',
  templateUrl: './add-service-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RktIconComponent, FormsModule, ReactiveFormsModule, RktFormModule, RktButtonDirective, TitleCasePipe, FormSelectFieldComponent],
})
export class AddServiceAccountComponent implements AfterViewChecked {
  store = inject(Store);

  createServiceAccountForm = new FormGroup({});

  isLoading = false;

  formSubmitError!: string;

  createServiceAccountFields = [
    formRow([
      formInput({
        key: 'name',
        label: 'Name',
        props: {
          type: 'email',
          required: true,
          pattern: Patterns.GENERIC_NAME,
          minLength: 1,
          maxLength: 40,
        },
        validation: {
          messages: {
            required: 'Name is required',
            pattern: 'Enter a valid name',
          },
        },
      }),
    ]),
    formRow([
      formRadioGroup({
        key: 'role',
        label: 'Role',
        className: 'multi-row-radio-group',
        props: {
          placeholder: 'Select',
          options: ROLES,
          required: true,
        },
      }),
    ]),
  ];

  formModel: FormModel<{ name: string; role: PermissionRoles }> = {};

  businessAccountId!: string;

  serviceAccount?: ServiceAccount;

  isEdit = false;

  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: ActiveModal,
    private ref: ChangeDetectorRef,
    private businessAccountService: BusinessAccountService,
  ) {}

  modalInitData({ businessAccountId, serviceAccount }: { businessAccountId: string; serviceAccount: ServiceAccount }) {
    this.businessAccountId = businessAccountId;
    if (serviceAccount) {
      this.isEdit = true;
      this.serviceAccount = serviceAccount;
      this.formModel = { ...this.formModel, name: serviceAccount.name, role: serviceAccount.roles.map((role) => role.name)[0] };
    }
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  onSubmit(): void {
    this.createServiceAccountForm.markAllAsTouched();

    if (this.createServiceAccountForm.invalid) {
      return;
    }

    const { name, role } = this.formModel;

    this.isLoading = true;
    this.formSubmitError = '';

    const serviceAccountData = {
      businessAccountId: this.businessAccountId,
      name: name!,
      roles: [{ name: role! }],
    };

    if (this.isEdit && this.serviceAccount) {
      this.businessAccountService
        .updateServiceAccount({ serviceAccountId: this.serviceAccount.id, serviceAccountData: { name: name!, roles: [{ name: role! }] } })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (updateResponse) => {
            this.activeModal.close(updateResponse);
            this.createServiceAccountForm.reset();
            this.isLoading = false;
          },
          error: (error) => {
            this.isLoading = false;
            this.formSubmitError = ErrorUtils.parseExtendedError(error);
          },
        });
    } else {
      this.businessAccountService
        .createServiceAccount({ serviceAccountData })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (createResponse) => {
            this.activeModal.close(createResponse);
            this.createServiceAccountForm.reset();
            this.isLoading = false;
          },
          error: (error) => {
            this.isLoading = false;
            this.formSubmitError = ErrorUtils.parseExtendedError(error);
          },
        });
    }
  }
}
